import { saveOperationLogApi } from '@/services/Applog/ApplogService';
import { laborFeeProcessNodes } from '@/utils/constants';

/**
 * 劳务费操作日志工具
 *
 * 模板解析机制说明：
 * 1. operationDesc 中使用 {{variable_name}} 格式定义模板变量
 * 2. extra 字段提供变量的实际值，格式为 { variable_name: actualValue }
 * 3. 展示时通过 operationLogParser.js 解析模板，将变量替换为实际值
 *
 * 示例：
 * - operationDesc: '付款申请，申请批次：{{batch_no}}'
 * - extra: { batch_no: '98ux1744964030235' }
 * - 最终展示: '付款申请，申请批次：98ux1744964030235'
 */

/**
 * 劳务费操作日志上报辅助函数
 * @param {Object} options - 日志参数
 * @param {number} options.entityId - 会议ID（entity_type为conference时使用）
 * @param {string} options.operationType - 操作类型
 * @param {string} options.operationDesc - 操作描述
 * @param {Object} options.extra - 扩展信息字段（可选）
 * @param {string} options.result - 操作结果: 'success' 或 'failed'
 * @param {Object} options.dataSnapshot - 数据快照（可选）
 * @returns {Promise} 返回操作结果
 */
export const logLaborFeeOperation = async options => {
    const { entityId, operationType, operationDesc, extra = null, result = 'success', dataSnapshot = null } = options;

    // 构建日志数据
    const logData = {
        entityType: 'conference',
        entityId,
        module: 'laborfee',
        portal: 'boss', // 端口标识：用于区分同一个 module 在不同端口。
        operationType,
        operationDesc,
        extra: extra ? JSON.stringify(extra) : null,
        result,
        dataSnapshot: dataSnapshot ? JSON.stringify(dataSnapshot) : null,
        memcard: null,
    };

    try {
        const response = await saveOperationLogApi(logData);
        console.log('劳务费操作日志上报成功', logData);
        return response;
    } catch (error) {
        console.error('劳务费操作日志上报失败', error, logData);
        throw error;
    }
};

/**
 * 医会议-劳务费管理-经办人发起
 * operation_type: laborfee_operator_submission
 */
export const logLaborFeeOperatorSubmission = async conferenceId => {
    return logLaborFeeOperation({
        entityId: conferenceId,
        operationType: 'laborfee_operator_submission',
        operationDesc: '经办人发起审批',
        result: 'success',
    });
};

/**
 * 实发劳务费审批流程 - 审核通过日志
 *
 * 适用于以下节点：
 * - 部门负责人审核（nodeId = 2）
 * - 分管高管审批（nodeId = 3）
 * - 总裁审批（nodeId = 4）
 * - 财务经理审核（nodeId = 5）
 * - 财务总监审批（nodeId = 6）
 *
 * operation_type: laborfee_approval
 * operation_desc: '{{nodeName}}，审核通过' 或者 '{{nodeName}}，审核通过：{{reason}}'
 *
 * @param {number} conferenceId - 会议ID
 * @param {number} nodeId - 流程节点ID
 * @param {string} [reason] - 通过理由（可选）
 */
export const logLaborFeeApprovalPassed = async (conferenceId, nodeId, reason) => {
    const node = laborFeeProcessNodes.find(item => item.nodeId === Number(nodeId));
    const nodeName = node?.name || '未知节点';

    let operationDesc = `${nodeName}，审核通过`;
    let extra = null;

    if (reason) {
        operationDesc += '：{{reason}}';
        extra = { reason };
    }

    return logLaborFeeOperation({
        entityId: conferenceId,
        operationType: 'laborfee_approval',
        operationDesc,
        extra,
        result: 'success',
    });
};

/**
 * 实发劳务费审批流程 - 审核驳回日志
 *
 * 适用于以下节点：
 * - 部门负责人审核（nodeId = 2）
 * - 分管高管审批（nodeId = 3）
 * - 总裁审批（nodeId = 4）
 * - 财务经理审核（nodeId = 5）
 * - 财务总监审批（nodeId = 6）
 *
 * operation_type: laborfee_approval
 * operation_desc: '{{nodeName}}，审核驳回：{{rejection_reason}}'
 *
 * @param {number} conferenceId - 会议ID
 * @param {number} nodeId - 流程节点ID
 * @param {string} rejectionReason - 驳回理由
 */
export const logLaborFeeApprovalRejected = async (conferenceId, nodeId, rejectionReason) => {
    const node = laborFeeProcessNodes.find(item => item.nodeId === Number(nodeId));
    const nodeName = node?.name || '未知节点';

    return logLaborFeeOperation({
        entityId: conferenceId,
        operationType: 'laborfee_approval',
        operationDesc: `${nodeName}，审核驳回：{{rejection_reason}}`,
        extra: { rejection_reason: rejectionReason },
        result: 'success',
    });
};

/**
 * 医会议-劳务费管理-经办人提交单据
 *
 * operation_type: laborfee_receipt_submission
 * operation_desc: '经办人提交单据，申请批次：{{batch_no}}'
 *
 * @param {number} conferenceId - 会议ID
 * @param {string} batchNo - 申请批次号
 */
export const logLaborFeeReceiptSubmission = async (conferenceId, batchNo) => {
    return logLaborFeeOperation({
        entityId: conferenceId,
        operationType: 'laborfee_receipt_submission',
        operationDesc: '经办人提交单据，申请批次：{{batch_no}}',
        extra: { batch_no: batchNo },
        result: 'success',
    });
};

/**
 * 发送"确认劳务信息"任务日志
 * @param {number} conferenceId - 会议ID
 */
export const logSendLaborFeeConfirmationTask = async conferenceId => {
    return logLaborFeeOperation({
        entityId: conferenceId,
        operationType: 'send_laborfee_confirmation_task',
        operationDesc: "发送'确认劳务信息'任务",
        result: 'success',
    });
};

/**
 * 实发劳务费审批通过日志
 * @param {number} conferenceId - 会议ID
 */
/**
 * @deprecated 已废弃，请使用 logLaborFeeApprovalPassed 代替
 */
export const logLaborFeeBizApproved = async conferenceId => {
    return logLaborFeeOperation({
        entityId: conferenceId,
        operationType: 'laborfee_biz_approved',
        operationDesc: '实发劳务费审批通过',
        result: 'success',
    });
};

/**
 * 财务部门审批通过日志
 * @param {number} conferenceId - 会议ID
 */
/**
 * @deprecated 已废弃，请使用 logLaborFeeApprovalPassed 代替
 */
export const logLaborFeeFinanceApproved = async conferenceId => {
    return logLaborFeeOperation({
        entityId: conferenceId,
        operationType: 'laborfee_finance_approved',
        operationDesc: '财务部门审批通过',
        result: 'success',
    });
};

/**
 * 付款申请日志
 * @param {number} conferenceId - 会议ID
 * @param {string} batchNo - 申请批次号
 */
export const logLaborFeePaymentApply = async (conferenceId, batchNo) => {
    return logLaborFeeOperation({
        entityId: conferenceId,
        operationType: 'laborfee_payment_apply',
        operationDesc: '付款申请，申请批次：{{batch_no}}',
        extra: { batch_no: batchNo },
        result: 'success',
    });
};

/**
 * 付款申请审批日志
 * @param {number} conferenceId - 会议ID
 * @param {string} batchNo - 申请批次号
 */
export const logLaborFeeFinancePaymentApproved = async (conferenceId, batchNo) => {
    return logLaborFeeOperation({
        entityId: conferenceId,
        operationType: 'laborfee_finance_payment_approved',
        operationDesc: '付款申请审批，申请批次：{{batch_no}}',
        extra: { batch_no: batchNo },
        result: 'success',
    });
};

/**
 * 确认付款日志（支持单个或多个确认付款）
 * @param {number} conferenceId - 会议ID
 * @param {Array} paymentRecords - 付款记录列表，格式：[{expert_id, laborfee_amount, individual_tax_amount, expert_name}, ...]
 */
export const logLaborFeeFinancePaymentConfirmed = async (conferenceId, paymentRecords) => {
    // 构建专家劳务描述文本：姓名1 ¥3000、姓名2 ¥3000
    const expertLaborDesc = paymentRecords
        .map(record => {
            const name = record.expert_name || '未知专家';
            const amount = record.laborfee_amount || 0;
            return `${name} ¥${amount}`;
        })
        .join('、');

    // 构建extra数据
    const extra = {
        payment_records: paymentRecords.map(record => ({
            expert_id: record.expert_id,
            laborfee_amount: record.laborfee_amount,
            individual_tax_amount: record.individual_tax_amount,
        })),
    };

    return logLaborFeeOperation({
        entityId: conferenceId,
        operationType: 'laborfee_finance_payment_confirmed',
        operationDesc: `确认付款，专家劳务：${expertLaborDesc}`,
        extra,
        result: 'success',
    });
};

/**
 * 实发劳务费审批重新审批日志
 * @param {number} conferenceId - 会议ID
 */
/**
 * @deprecated 已废弃，新的审批流程统一使用 laborfee_approval 相关方法
 */
export const logLaborFeeBizApprovalReset = async conferenceId => {
    return logLaborFeeOperation({
        entityId: conferenceId,
        operationType: 'laborfee_biz_approval_reset',
        operationDesc: '实发劳务费审批重新审批',
        result: 'success',
    });
};

/**
 * 财务部门审批驳回日志
 * @param {number} conferenceId - 会议ID
 * @param {string} rejectionReason - 驳回理由
 */
/**
 * @deprecated 已废弃，请使用 logLaborFeeApprovalRejected 代替
 */
export const logLaborFeeFinanceApprovalReject = async (conferenceId, rejectionReason) => {
    return logLaborFeeOperation({
        entityId: conferenceId,
        operationType: 'laborfee_finance_approval_reject',
        operationDesc: '财务部门审批驳回，驳回理由：{{rejection_reason}}',
        extra: { rejection_reason: rejectionReason },
        result: 'success',
    });
};

/**
 * 实发劳务费已修改重新提交日志
 * @param {number} conferenceId - 会议ID
 */
/**
 * @deprecated 已废弃，新的审批流程统一使用 laborfee_approval 相关方法
 */
export const logLaborFeeBizApprovedAgain = async conferenceId => {
    return logLaborFeeOperation({
        entityId: conferenceId,
        operationType: 'laborfee_biz_approved_again',
        operationDesc: '实发劳务费已修改重新提交',
        result: 'success',
    });
};
